import {
  GarnerRecommendedProvidersClientV1,
  GetProviders200Response,
} from '@garner-health/api-client-svc-r12n/r12n-providers/r12n-providers-v1';
import { AsyncResult, failure, success } from '@garner-health/lib-ts-common';

import type { Plan } from '~/auth';
import createLogger from '~/logging';
import { Position, removeEmptyFields } from '~/util';

import {
  baseClientOptions,
  demoClientOptions,
  getDefaultHeaders,
  getRateLimitReset,
  isRateLimitedError,
} from './common';
import { Metric } from './types';

const log = createLogger(__filename);

export type ProviderType = 'physician' | 'facility';

/**
 * The type of the providers list. Determines which fields to include on table and how table can be sorted
 */
export type ProvidersResponseSetType = ProviderType | 'name' | 'hcsc_compliant';

export type GetProvidersParams = {
  specialty?: string;
  name?: string;
  networkId: string;
  location: Position;
  plan: Plan;
  isDemo: boolean;
  language?: string;
  gender?: 'male' | 'female';
  isApp?: boolean;
  limit?: number;
  includeLowQualityProviders?: boolean;
  providerSearchSet?: 'in_network' | 'top_provider';
};

export type ProviderResultItem = {
  id: string;
  type: ProviderType;
  firstName?: string;
  lastName?: string;
  credentials?: string;
  organizationName?: string;
  specialty: string;
  distanceMi?: number;
  overallScore?: number;
  location: {
    id: string;
    position: Position;
    name?: string;
    facilityName?: string;
    providerCorporationName?: string;
    allProviderCorporations?: string[];
    lines: string[];
    state: string;
    zipCode: string;
    city: string;
  };
  specialties: string[];
  phoneNumber?: string;
  isTopProvider?: boolean;
  metrics?: Metric[];
  providerCorporationName?: string;
};

export type GetProvidersResult = {
  providers: ProviderResultItem[];
  responseSetType?: ProvidersResponseSetType;
};

class RecommendedProvidersClient {
  readonly client: GarnerRecommendedProvidersClientV1;
  readonly demoClient: GarnerRecommendedProvidersClientV1;

  constructor(options = baseClientOptions, demoOptions = demoClientOptions) {
    this.client = new GarnerRecommendedProvidersClientV1(options);
    this.demoClient = new GarnerRecommendedProvidersClientV1(demoOptions);
  }

  async getProviders({
    specialty,
    name,
    networkId,
    plan,
    isDemo,
    language,
    gender,
    isApp,
    location: { lat, lng },
    includeLowQualityProviders,
    providerSearchSet,
    limit,
  }: GetProvidersParams): AsyncResult<GetProvidersResult, 'rateLimited'> {
    const query = removeEmptyFields({
      specialty,
      name,
      networkId,
      lat,
      lng,
      plan,
      language,
      gender,
      isApp,
      includeLowQualityProviders,
      providerSearchSet,
      limit,
    });
    return await (isDemo ? this.demoClient : this.client)
      .get('/providers', {
        headers: await getDefaultHeaders(),
        query: { ...query, allowIncompleteDirectory: false },
      })
      .onStatus(200, ({ json }) => {
        return success(this.buildGetProvidersResult({ json, name }));
      })
      .onStatus(422, r => {
        log.error(
          {
            req: { id: r.json.requestId },
            strSpecialty: specialty,
            networkId,
            numLat: lat,
            numLng: lng,
            strLanguage: language,
            strGender: gender,
            message: r.json.message,
          },
          'Bad request when getting providers',
        );
        return success({ providers: [] });
      })
      .catch((err: unknown) => {
        // A rate-limited failure rather than `success({ providers: [] })`: a throttled search must
        // not be readable as a legitimate empty result.
        if (isRateLimitedError(err)) {
          log.warn(
            {
              strSpecialty: specialty,
              networkId,
              // The response body is empty and the gateway suppresses `x-envoy-ratelimited`, so the
              // status is the only signal.
              strRateLimitReset: getRateLimitReset(err.response.headers),
            },
            'Rate limited when getting providers',
          );
          return failure('rateLimited');
        }
        log.error(
          {
            err,
            strSpecialty: specialty,
            networkId,
            numLat: lat,
            numLng: lng,
            strLanguage: language,
            strGender: gender,
          },
          'Error getting providers',
        );
        throw err;
      });
  }

  buildGetProvidersResult({ json, name }: { json: GetProviders200Response; name?: string }): GetProvidersResult {
    const providersResult = json.providers;
    // If empty response, return as is
    if (providersResult.length === 0) return { providers: [] };

    const responseSetType: ProvidersResponseSetType = name ? 'name' : providersResult[0].type;

    // If facilities, don't collapse list
    if (responseSetType === 'facility') {
      return {
        providers: providersResult.map(provider => ({
          ...provider,
          specialties: [provider.specialty],
        })),
        responseSetType,
      };
    }

    const providersMap = new Map<string, ProviderResultItem>();

    // If multiple providers in results have same id, collate into one object with multiple specialties
    for (const provider of providersResult) {
      if (!provider.networkStatus) continue;

      const existing = providersMap.get(provider.id);

      if (!existing) {
        providersMap.set(provider.id, {
          ...provider,
          specialties: [provider.specialty],
        });
      } else if (!existing.specialties.includes(provider.specialty)) {
        existing.specialties.push(provider.specialty);
      }
    }

    return {
      providers: [...providersMap.values()],
      responseSetType,
    };
  }
}

export const recommendedProvidersClient = new RecommendedProvidersClient();
